import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../../../../firebase/AuthContext";
import {
  batchDeleteFaq,
  batchUpdateStatus,
  createFaq,
  deleteFaq,
  getAllTags,
  getFaqStats,
  listCategories,
  listFaq,
  listFaqVersions,
  rollbackFaq,
  updateFaq,
} from "../api/faq";
import FaqForm from "../components/FaqForm";
import TextDiff from "../components/TextDiff";

const EMPTY_FORM = {
  question: "",
  answer: "",
  language: "en",
  source: "manual",
  source_url: "",
  status: "pending",
  tags: [],
};

const STATUSES = ["approved", "pending", "rejected", "deleted"];
const SOURCES = ["manual", "excel_import", "ai_generated"];
const LANGUAGES = ["en", "cn", "tw", "jp", "vn"];

const STATUS_META = {
  approved: {
    label: "Approved",
    border: "border-l-emerald-500",
    chip: "bg-emerald-500",
    icon: "M5 13l4 4L19 7",
  },
  pending: {
    label: "Pending",
    border: "border-l-orange-500",
    chip: "bg-orange-500",
    icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  rejected: {
    label: "Rejected",
    border: "border-l-red-500",
    chip: "bg-red-500",
    icon: "M6 18L18 6M6 6l12 12",
  },
  deleted: {
    label: "Deleted",
    border: "border-l-gray-400",
    chip: "bg-gray-400",
    icon: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  },
};

const STATUS_BADGE_STYLES = {
  approved: "bg-emerald-100 text-emerald-700",
  pending: "bg-orange-100 text-orange-700",
  rejected: "bg-red-100 text-red-700",
  deleted: "bg-gray-200 text-gray-600",
};

const Spinner = () => (
  <div className="flex justify-center py-8">
    <svg
      className="animate-spin h-8 w-8 text-orange-500"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  </div>
);

const PAGE_SIZE = 20;

function FaqListTab() {
  const { currentUser, hasRole } = useAuth();
  const canEdit = hasRole && hasRole("knowledge.faq.review");

  const [faqs, setFaqs] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const [filters, setFilters] = useState({
    status: "",
    language: "",
    source: "",
    q: "",
    tags: [],
    sort_by: "display_id",
    order: "asc",
  });
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const tagFilterRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchStatus, setBatchStatus] = useState(STATUSES[0]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  const [historyOpenId, setHistoryOpenId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [expandedVersionNum, setExpandedVersionNum] = useState(null);

  const load = useCallback(
    async (f = filters, targetPage = page, targetPageSize = pageSize) => {
      if (!currentUser) return;
      setLoading(true);
      setError("");
      try {
        const token = await currentUser.getIdToken();
        const cleaned = Object.fromEntries(
          Object.entries(f).filter(([, v]) =>
            Array.isArray(v) ? v.length > 0 : v,
          ),
        );
        const [faqResult, stats, tags, cats] = await Promise.all([
          listFaq(token, {
            ...cleaned,
            page: targetPage,
            page_size: targetPageSize,
          }),
          getFaqStats(token),
          getAllTags(token),
          listCategories(token),
        ]);
        setFaqs(faqResult.items || []);
        setTotal(faqResult.total ?? 0);
        setPage(faqResult.page ?? targetPage);
        setPageSize(faqResult.page_size ?? targetPageSize);
        setStatusCounts(stats || {});
        setAvailableTags(tags || []);
        setCategories(cats || []);
        setSelectedIds(new Set());
      } catch (err) {
        console.error("[FaqListTab] Failed to load FAQ list:", err);
        setError("Failed to load the FAQ list.");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!tagFilterOpen) return;
    const handleClickOutside = (e) => {
      if (tagFilterRef.current && !tagFilterRef.current.contains(e.target)) {
        setTagFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [tagFilterOpen]);

  const applyFilters = (next) => {
    setFilters(next);
    load(next, 1);
  };

  const goToPage = (nextPage) => {
    load(filters, nextPage, pageSize);
  };

  const changePageSize = (nextPageSize) => {
    load(filters, 1, nextPageSize);
  };

  const toggleTagFilter = (tag) => {
    const next = {
      ...filters,
      tags: filters.tags.includes(tag)
        ? filters.tags.filter((t) => t !== tag)
        : [...filters.tags, tag],
    };
    applyFilters(next);
  };

  const toggleExpand = (id) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (faq) => {
    const snapshot = {
      question: faq.question,
      answer: faq.answer,
      language: faq.language,
      source: faq.source,
      source_url: faq.source_url ?? "",
      status: faq.status,
      tags: faq.tags || [],
    };
    setEditingId(faq.question_id);
    setForm(snapshot);
    setOriginalForm(snapshot);
    setExpandedId(faq.question_id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setOriginalForm(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formSubmitting) return;
    setFormSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      if (editingId) {
        // Only send fields that actually changed, so unrelated fields
        // don't trigger a spurious new version.
        const changed = Object.fromEntries(
          Object.keys(form)
            .filter(
              (key) =>
                JSON.stringify(form[key]) !==
                JSON.stringify(originalForm?.[key]),
            )
            .map((key) => [key, form[key]]),
        );
        if ("source_url" in changed) {
          changed.source_url = changed.source_url || null;
        }
        if (Object.keys(changed).length > 0) {
          await updateFaq(token, editingId, changed);
        }
        // If the version history panel is open for the entry we just saved,
        // refresh it too — otherwise it keeps showing the pre-save snapshot
        // until the user manually collapses and re-expands it.
        if (historyOpenId === editingId) {
          setVersionsLoading(true);
          try {
            const refreshedVersions = await listFaqVersions(token, editingId);
            setVersions(refreshedVersions);
            const previous = refreshedVersions.find(
              (v) => v.version_num !== refreshedVersions[0]?.version_num,
            );
            setExpandedVersionNum(previous?.version_num ?? null);
          } finally {
            setVersionsLoading(false);
          }
        }
        setEditingId(null);
        setOriginalForm(null);
      } else {
        const body = {
          ...form,
          source_url: form.source_url || null,
          category_ids: [],
          image_urls: [],
        };
        await createFaq(token, body);
        setShowForm(false);
      }
      await load(filters, page, pageSize);
    } catch (err) {
      console.error("[FaqListTab] Failed to save FAQ:", err);
      setError("Failed to save this FAQ entry. Please try again.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async (identifier) => {
    if (!window.confirm("Delete this FAQ entry?")) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteFaq(token, identifier);
      await load(filters, page, pageSize);
    } catch (err) {
      console.error("[FaqListTab] Failed to delete FAQ:", err);
      setError("Failed to delete this FAQ entry. Please try again.");
    }
  };

  const toggleHistory = async (identifier) => {
    if (historyOpenId === identifier) {
      setHistoryOpenId(null);
      return;
    }
    setHistoryOpenId(identifier);
    setVersionsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const result = await listFaqVersions(token, identifier);
      setVersions(result);
      const previous = result.find(
        (v) => v.version_num !== result[0]?.version_num,
      );
      setExpandedVersionNum(previous?.version_num ?? null);
    } catch (err) {
      console.error("[FaqListTab] Failed to load version history:", err);
      setError("Failed to load version history.");
    } finally {
      setVersionsLoading(false);
    }
  };

  const toggleVersionExpand = (versionNum) => {
    setExpandedVersionNum((current) =>
      current === versionNum ? null : versionNum,
    );
  };

  const handleRollback = async (identifier, versionNum) => {
    if (
      !window.confirm(
        `Roll back to version ${versionNum}? This creates a new version with that content.`,
      )
    )
      return;
    try {
      const token = await currentUser.getIdToken();
      await rollbackFaq(token, identifier, versionNum);
      setHistoryOpenId(null);
      await load(filters, page, pageSize);
    } catch (err) {
      console.error("[FaqListTab] Failed to roll back FAQ:", err);
      setError("Failed to roll back this FAQ entry. Please try again.");
    }
  };

  const toggleSelected = (identifier) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(identifier)) {
        next.delete(identifier);
      } else {
        next.add(identifier);
      }
      return next;
    });
  };

  const allSelected = faqs.length > 0 && selectedIds.size === faqs.length;
  const toggleSelectAll = () => {
    setSelectedIds(
      allSelected ? new Set() : new Set(faqs.map((f) => f.question_id)),
    );
  };

  const handleBatchStatusApply = async () => {
    setBatchSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      await batchUpdateStatus(token, Array.from(selectedIds), batchStatus);
      await load(filters, page, pageSize);
    } catch (err) {
      console.error("[FaqListTab] Failed to batch update status:", err);
      setError("Failed to update status for the selected entries.");
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleBatchDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} selected FAQ entries?`))
      return;
    setBatchSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      await batchDeleteFaq(token, Array.from(selectedIds));
      await load(filters, page, pageSize);
    } catch (err) {
      console.error("[FaqListTab] Failed to batch delete:", err);
      setError("Failed to delete the selected entries.");
    } finally {
      setBatchSubmitting(false);
    }
  };

  const totalCount = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const categoryName = (id) =>
    categories.find((c) => c.category_id === id)?.category_name || id;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Status stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 dark:border-l-gray-600 p-4 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
              Total
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
              {totalCount}
            </p>
          </div>
          <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-white bg-gray-400">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
        </div>
        {STATUSES.map((s) => {
          const meta = STATUS_META[s];
          return (
            <div
              key={s}
              className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700  p-4 flex items-center justify-between shadow-sm`}
            >
              <div>
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                  {meta.label}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                  {statusCounts[s] ?? 0}
                </p>
              </div>
              <div
                className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-white ${meta.chip}`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={meta.icon}
                  />
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-4 border border-gray-300/50 bg-white shadow-xl rounded-xl p-4">
        <p className="text-sm text-gray-600">
          Browse, filter, and manage FAQ entries. Click a row to expand and read
          the full answer.
          {canEdit &&
            " Select rows with the checkboxes to update their status or delete them in bulk."}
        </p>
        {canEdit && (
          <button
            onClick={startCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors shrink-0"
          >
            New FAQ
          </button>
        )}
      </div>

      {/* Toolbar: filters + (when editable) bulk actions on selected rows */}
      <div className="flex flex-col gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters(filters);
          }}
          className="flex gap-2 flex-wrap items-center"
        >
          <select
            value={filters.status}
            onChange={(e) =>
              applyFilters({ ...filters, status: e.target.value })
            }
            className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={filters.language}
            onChange={(e) =>
              applyFilters({ ...filters, language: e.target.value })
            }
            className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          >
            <option value="">All languages</option>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <select
            value={filters.source}
            onChange={(e) =>
              applyFilters({ ...filters, source: e.target.value })
            }
            className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          >
            <option value="">All sources</option>
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={`${filters.sort_by}:${filters.order}`}
            onChange={(e) => {
              const [sort_by, order] = e.target.value.split(":");
              applyFilters({ ...filters, sort_by, order });
            }}
            className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          >
            <option value="display_id:asc">ID (ascending)</option>
            <option value="display_id:desc">ID (descending)</option>
            <option value="created_at:desc">Newest first</option>
            <option value="created_at:asc">Oldest first</option>
            <option value="updated_at:desc">Recently updated</option>
          </select>
          <div className="relative" ref={tagFilterRef}>
            <button
              type="button"
              onClick={() => setTagFilterOpen((open) => !open)}
              className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
            >
              {filters.tags.length > 0
                ? `Tags (${filters.tags.length})`
                : "All tags"}
            </button>
            {tagFilterOpen && (
              <div className="absolute z-10 mt-1 w-56 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 flex flex-col gap-1">
                {availableTags.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 px-2 py-1">
                    No tags yet.
                  </p>
                )}
                {availableTags.map((tag) => (
                  <label
                    key={tag}
                    className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filters.tags.includes(tag)}
                      onChange={() => toggleTagFilter(tag)}
                    />
                    {tag}
                  </label>
                ))}
              </div>
            )}
          </div>
          <input
            placeholder="Search keyword (question, answer, tags)"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm flex-1 min-w-[160px] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          />
          <button
            type="submit"
            className="px-3 py-1.5 text-sm font-medium text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800 border border-orange-300 dark:border-orange-900/40 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
          >
            Filter
          </button>
        </form>

        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap border-t border-gray-100 dark:border-gray-700 pt-3">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {selectedIds.size > 0 ? (
                <span className="text-orange-700 dark:text-orange-400 font-medium">
                  {selectedIds.size} selected
                </span>
              ) : (
                "Check rows below to select FAQs, then update their status or delete them here."
              )}
            </span>
            <select
              value={batchStatus}
              onChange={(e) => setBatchStatus(e.target.value)}
              disabled={selectedIds.size === 0}
              className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:opacity-50"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              onClick={handleBatchStatusApply}
              disabled={batchSubmitting || selectedIds.size === 0}
              className="px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50 transition-colors"
            >
              Set Status
            </button>
            <button
              onClick={handleBatchDelete}
              disabled={batchSubmitting || selectedIds.size === 0}
              className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 transition-colors"
            >
              Delete Selected
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowForm(false)}
          />
          <div className="relative w-[40%] max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl animate-slideUp">
            <FaqForm
              title="New FAQ"
              form={form}
              onChange={setForm}
              onSubmit={handleSubmit}
              onCancel={() => setShowForm(false)}
              submitting={formSubmitting}
              languages={LANGUAGES}
              statuses={STATUSES}
            />
          </div>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : faqs.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No FAQ entries found.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm text-left">
              <thead className="text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-900/40">
                <tr>
                  {canEdit && (
                    <th className="py-2.5 px-4 w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th className="py-2.5 px-4">ID</th>
                  <th className="py-2.5 px-4">Language</th>
                  <th className="py-2.5 px-4">Question</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4">Source</th>
                  {canEdit && <th className="py-2.5 px-4">Actions</th>}
                  <th className="py-2.5 px-4 w-8" />
                </tr>
              </thead>
              <tbody>
                {faqs.map((faq) => {
                  const isExpanded = expandedId === faq.question_id;
                  return (
                    <Fragment key={faq.question_id}>
                      <tr
                        onClick={() => toggleExpand(faq.question_id)}
                        className="bg-white border-b border-gray-100 dark:border-gray-700/50 cursor-pointer hover:bg-orange-50/40 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        {canEdit && (
                          <td
                            className="py-2.5 px-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(faq.question_id)}
                              onChange={() => toggleSelected(faq.question_id)}
                            />
                          </td>
                        )}
                        <td className="py-2.5 px-4 text-gray-500 dark:text-gray-400">
                          {faq.display_id}
                        </td>
                        <td className="py-2.5 px-4 text-gray-500 dark:text-gray-400">
                          {faq.language || "—"}
                        </td>
                        <td className="py-2.5 px-4 text-gray-900 dark:text-gray-100 max-w-xs truncate">
                          {faq.question}
                        </td>
                        <td className="py-2.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              STATUS_BADGE_STYLES[faq.status] ||
                              "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {faq.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-gray-500 dark:text-gray-400">
                          {faq.source || "—"}
                        </td>
                        {canEdit && (
                          <td
                            className="py-2.5 px-4 flex gap-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => startEdit(faq)}
                              title="Edit"
                              className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(faq.question_id)}
                              title="Delete"
                              className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </td>
                        )}
                        <td className="py-2.5 px-4">
                          <svg
                            className={`w-4 h-4 text-gray-400 transition-transform ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700/50">
                          <td colSpan={canEdit ? 8 : 6} className="py-3 px-4">
                            {editingId === faq.question_id ? (
                              <FaqForm
                                title="Edit FAQ"
                                form={form}
                                onChange={setForm}
                                onSubmit={handleSubmit}
                                onCancel={cancelEdit}
                                submitting={formSubmitting}
                                languages={LANGUAGES}
                                statuses={STATUSES}
                              />
                            ) : (
                              <>
                                <div className="text-gray-700 dark:text-gray-300 break-words [&_table]:border-collapse [&_table]:my-2 [&_th]:border [&_th]:border-slate-300 dark:[&_th]:border-gray-600 [&_th]:bg-slate-100 dark:[&_th]:bg-gray-700 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-slate-300 dark:[&_td]:border-gray-600 [&_td]:px-2 [&_td]:py-1">
                                  <span className="font-medium text-gray-500 dark:text-gray-400">
                                    Answer:{" "}
                                  </span>
                                  {/* singleTilde off: a spec range like `-25~+150° C` is not strikethrough. */}
                                  <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>
                                    {faq.answer}
                                  </ReactMarkdown>
                                </div>
                                {faq.category_ids &&
                                  faq.category_ids.length > 0 && (
                                    <p className="mt-2 text-sm">
                                      <span className="font-medium text-gray-500 dark:text-gray-400">
                                        Categories:{" "}
                                      </span>
                                      {faq.category_ids
                                        .map(categoryName)
                                        .join(", ")}
                                    </p>
                                  )}
                                {faq.tags && faq.tags.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {faq.tags.map((tag) => (
                                      <span
                                        key={tag}
                                        className="px-2 py-0.5 rounded-full text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {faq.sources && faq.sources.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                      Sources
                                    </p>
                                    <ul className="flex flex-col gap-1">
                                      {faq.sources.map((source, i) => (
                                        <li key={i} className="flex gap-1.5">
                                          <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                                            [{i + 1}]
                                          </span>
                                          <a
                                            href={source.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-sm text-orange-600 dark:text-orange-400 hover:underline break-all"
                                          >
                                            {source.title || source.domain}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {faq.source_url && (
                                  <p className="mt-2 text-sm">
                                    <span className="font-medium text-gray-500 dark:text-gray-400">
                                      Source:{" "}
                                    </span>
                                    <a
                                      href={faq.source_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-orange-600 dark:text-orange-400 hover:underline break-all"
                                    >
                                      {faq.source_url}
                                    </a>
                                  </p>
                                )}
                                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-2">
                                  <span>
                                    Version {faq.current_version} · Last updated{" "}
                                    {faq.updated_at
                                      ? new Date(
                                          faq.updated_at,
                                        ).toLocaleString()
                                      : "—"}
                                    {faq.last_edited_by && (
                                      <> by {faq.last_edited_by}</>
                                    )}
                                  </span>
                                  {canEdit && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleHistory(faq.question_id);
                                      }}
                                      className="text-orange-600 dark:text-orange-400 hover:underline"
                                    >
                                      {historyOpenId === faq.question_id
                                        ? "Hide history"
                                        : "View history"}
                                    </button>
                                  )}
                                </p>
                                {historyOpenId === faq.question_id && (
                                  <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                    {versionsLoading && (
                                      <p className="p-3 text-sm text-gray-400 dark:text-gray-500">
                                        Loading versions...
                                      </p>
                                    )}
                                    {!versionsLoading &&
                                      versions.every(
                                        (v) =>
                                          v.version_num === faq.current_version,
                                      ) && (
                                        <p className="p-3 text-sm text-gray-400 dark:text-gray-500">
                                          No previous versions.
                                        </p>
                                      )}
                                    {!versionsLoading &&
                                      versions
                                        .filter(
                                          (v) =>
                                            v.version_num !==
                                            faq.current_version,
                                        )
                                        .map((v) => {
                                          const isVersionExpanded =
                                            expandedVersionNum ===
                                            v.version_num;
                                          const currentVersion = versions.find(
                                            (cv) =>
                                              cv.version_num ===
                                              faq.current_version,
                                          );
                                          return (
                                            <div
                                              key={v.version_id}
                                              className="text-sm"
                                            >
                                              <div
                                                onClick={() =>
                                                  toggleVersionExpand(
                                                    v.version_num,
                                                  )
                                                }
                                                className="p-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                                              >
                                                <div>
                                                  <span className="font-medium text-gray-700 dark:text-gray-200">
                                                    Version {v.version_num}
                                                  </span>
                                                  <span className="ml-2 text-gray-400 dark:text-gray-500 text-xs">
                                                    {v.change_note}
                                                  </span>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                  {v.edited_by && (
                                                    <span
                                                      className="text-gray-400 dark:text-gray-500 text-xs"
                                                      title="Edited by"
                                                    >
                                                      by {v.edited_by}
                                                    </span>
                                                  )}
                                                  <span className="text-gray-400 dark:text-gray-500 text-xs">
                                                    {new Date(
                                                      v.edited_at,
                                                    ).toLocaleString()}
                                                  </span>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRollback(
                                                        faq.question_id,
                                                        v.version_num,
                                                      );
                                                    }}
                                                    className="px-3 py-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800 border border-orange-300 dark:border-orange-900/40 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
                                                  >
                                                    Roll back
                                                  </button>
                                                </div>
                                              </div>
                                              {isVersionExpanded &&
                                                currentVersion && (
                                                  <div className="px-3 pb-3">
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                                                      Showing differences from
                                                      current (Version{" "}
                                                      {faq.current_version})
                                                    </p>
                                                    <TextDiff
                                                      oldText={v.question}
                                                      newText={
                                                        currentVersion.question
                                                      }
                                                    />
                                                    <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 rounded">
                                                      <TextDiff
                                                        oldText={v.answer}
                                                        newText={
                                                          currentVersion.answer
                                                        }
                                                      />
                                                    </div>
                                                  </div>
                                                )}
                                            </div>
                                          );
                                        })}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between flex-wrap gap-3 bg-white dark:bg-gray-800 rounded-xl px-6 py-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages} ({total} entries)
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={(e) => changePageSize(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-orange-400"
                  >
                    {[10, 20, 50].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1 || loading}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  // Show pages around the current page
                  let p;
                  if (totalPages <= 7) {
                    p = i + 1;
                  } else if (page <= 4) {
                    p = i + 1;
                  } else if (page >= totalPages - 3) {
                    p = totalPages - 6 + i;
                  } else {
                    p = page - 3 + i;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => goToPage(p)}
                      disabled={loading}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                        p === page
                          ? "bg-orange-500 text-white shadow-sm"
                          : "text-gray-600 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-500"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages || loading}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default FaqListTab;
